import { create } from 'zustand';
import { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useSSE } from '../app/providers/SseProvider';
import { useConversations as useConversationsQuery } from '../hooks/api/useConversations';
import { useOptionalTargetId } from './projectStore';
import {
  findChatTabByThreadId,
  useChatTabs,
  useChatTabsStore,
} from './chatTabsStore';
import { toasterApi } from '@terra/ui-design-system';
import {
  CONVERSATION_CREATED_SSE_EVENT,
  CONVERSATION_UPDATED_SSE_EVENT,
} from '@terra/shared-dto-chat-types';
import { resolveCustomConversationTitle } from '../components/chat/chatTabDisplayTitle';
import {
  conversationForCacheWithPreservedTitle,
  preserveCustomTitleOnConversationUpdate,
} from './preserveCustomConversationTitle';

export type DeleteConversationResponseDTO = {
  success: boolean;
};

interface ConversationsState {
  activeConversationId: string | null;
  setActiveConversation: (id: string | null) => void;
}

export const useConversationsStore = create<ConversationsState>((set) => ({
  activeConversationId: null,
  setActiveConversation: (id) => {
    set({ activeConversationId: id });
  },
}));

export function useConversations({
  targetId,
}: {
  targetId?: string;
} = {}) {
  const { activeConversationId, setActiveConversation } =
    useConversationsStore();

  const {
    conversations,
    isLoading,
    error,
    refetch,
    deleteConversation: deleteConversationMutation,
    updateConversation: updateConversationMutation,
  } = useConversationsQuery({ targetId });

  const deleteConversation = useCallback(
    async (id: string) => {
      deleteConversationMutation(id);
      if (activeConversationId === id) {
        setActiveConversation(null);
      }
    },
    [activeConversationId, deleteConversationMutation, setActiveConversation],
  );

  const updateConversation = useCallback(
    async ({
      conversationId,
      title,
      description,
    }: {
      conversationId: string;
      title?: string;
      description?: string;
    }) => {
      return updateConversationMutation({
        conversationId,
        title,
        description,
      });
    },
    [updateConversationMutation],
  );

  const getConversation = useCallback(
    (id: string) => {
      return conversations.find((conversation) => conversation.id === id);
    },
    [conversations],
  );

  const getRecentConversations = useCallback(
    (limit = 5) => {
      return conversations.slice(0, limit);
    },
    [conversations],
  );

  return {
    conversations,
    activeConversationId,
    isLoading,
    error,
    refetch,
    deleteConversation,
    updateConversation,
    getConversation,
    getRecentConversations,
    setActiveConversation,
  };
}

export function useSubscribeToConversations() {
  const tCopilot = useTranslations('chat.copilot');
  const { updateChatTab } = useChatTabs();
  const targetId = useOptionalTargetId();
  const { subscribe } = useSSE();
  const {
    addConversationToCache,
    updateConversationInCache,
    updateConversation,
  } = useConversationsQuery({ targetId });

  useEffect(() => {
    const unsubscribeCreated = subscribe(
      CONVERSATION_CREATED_SSE_EVENT,
      (event) => {
        const newConversation = event.conversation;

        if (!newConversation) {
          return;
        }

        if (targetId && newConversation.targetId !== targetId) {
          return;
        }

        if (newConversation.threadId) {
          const defaultTitle = tCopilot('newChatTitle');
          const existingTab = findChatTabByThreadId({
            chatTabs: useChatTabsStore.getState().chatTabs,
            threadId: newConversation.threadId,
          });
          const customTitle = resolveCustomConversationTitle({
            tab: existingTab,
          });
          const title = customTitle ?? newConversation.title ?? defaultTitle;

          updateChatTab(newConversation.threadId, {
            conversationId: newConversation.id,
            title,
            ...(customTitle ? { hasCustomTitle: true } : {}),
          });

          if (customTitle && customTitle !== (newConversation.title ?? '')) {
            void updateConversation({
              conversationId: newConversation.id,
              title: customTitle,
            }).catch((error: unknown) => {
              toasterApi.error({
                title: tCopilot('renameConversationFailed'),
                description:
                  error instanceof Error && error.message
                    ? error.message
                    : undefined,
              });
            });
          }

          addConversationToCache(
            conversationForCacheWithPreservedTitle({
              existingTab:
                customTitle && existingTab
                  ? {
                      ...existingTab,
                      title: customTitle,
                      hasCustomTitle: true,
                    }
                  : existingTab,
              conversation: newConversation,
            }),
          );
        } else {
          addConversationToCache(newConversation);
        }
      },
    );

    const unsubscribeUpdated = subscribe(
      CONVERSATION_UPDATED_SSE_EVENT,
      (event) => {
        const updatedConversation = event.conversation;

        if (!updatedConversation) {
          return;
        }

        if (targetId && updatedConversation.targetId !== targetId) {
          return;
        }

        const defaultTitle = tCopilot('newChatTitle');
        const existingTab = updatedConversation.threadId
          ? findChatTabByThreadId({
              chatTabs: useChatTabsStore.getState().chatTabs,
              threadId: updatedConversation.threadId,
            })
          : undefined;

        updateConversationInCache(
          conversationForCacheWithPreservedTitle({
            existingTab,
            conversation: updatedConversation,
          }),
        );

        if (updatedConversation.threadId) {
          updateChatTab(
            updatedConversation.threadId,
            preserveCustomTitleOnConversationUpdate({
              existingTab,
              conversationId: updatedConversation.id,
              serverTitle: updatedConversation.title,
              defaultTitle,
            }),
          );
        }
      },
    );

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
    };
  }, [
    targetId,
    subscribe,
    addConversationToCache,
    updateConversationInCache,
    updateConversation,
    updateChatTab,
    tCopilot,
  ]);
}
